import { useMemo } from "react";
import Modal from "../../../components/base/Modal";
import EntityForm from "./EntityForm";
import type { FormField } from "./ApiCrudSection";
import { buildThemeClasses, MAINTENANCE_STATUSES, MAINTENANCE_TYPES, WORKSHOP_MODES } from "../theme";
import { useMxLabels } from "../hooks/useMxLabels";
import type {
  MaintenanceAttachment,
  MaintenancePart,
  MaintenanceRecord,
  MaintenanceServiceItem,
  MaintenanceType,
  VehicleOption,
  Workshop,
} from "../types";

// Safely parse a JSON array emitted by a `repeat` form field.
function parseRows<T>(raw?: string): T[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

const DATE_FIELDS = ["lastMaintenanceDate", "scheduledDate", "nextMaintenanceDate"];

/** Valores iniciales del formulario: fechas a `YYYY-MM-DD` y fotos a líneas. */
function initialValues(record?: MaintenanceRecord | null): Record<string, unknown> | undefined {
  if (!record) return undefined;
  const out: Record<string, unknown> = { ...record };
  for (const k of DATE_FIELDS) {
    const v = record[k as keyof MaintenanceRecord];
    out[k] = typeof v === "string" ? v.slice(0, 10) : "";
  }
  out.photos = (record.photos || []).join("\n");
  return out;
}

export interface MaintenanceFormModalProps {
  record?: MaintenanceRecord | null;
  /** Tipo por omisión al crear (sección programado/solicitado/sugerido). */
  defaultType?: MaintenanceType;
  /** Agrega los campos de orden de trabajo (mecánico, refacciones…). */
  workOrder?: boolean;
  vehicles: VehicleOption[];
  workshops: Workshop[];
  onClose: () => void;
  onSubmit: (payload: Partial<MaintenanceRecord>, id?: string) => Promise<void>;
}

// Alta / edición de un mantenimiento con el esquema del API (el mismo que
// antes vivía en MaintenanceListSection), más las fotos de evidencia.
export default function MaintenanceFormModal({ record, defaultType, workOrder = false, vehicles, workshops, onClose, onSubmit }: MaintenanceFormModalProps) {
  const L = useMxLabels();
  const { t } = L;
  const tc = buildThemeClasses();
  const isWorkOrder = workOrder || !!record?.assignedMechanic || !!record?.parts?.length;

  const fields: FormField[] = useMemo(() => {
    const base: FormField[] = [
      { key: "vehicleId", label: t("mantenimiento.fields.vehicle"), type: "select", required: true, options: vehicles.map((v) => ({ value: v.id, label: v.plate ? `${v.label} · ${v.plate}` : v.label })) },
      { key: "type", label: t("mantenimiento.fields.type"), type: "select", required: true, defaultValue: defaultType ?? "programado", options: MAINTENANCE_TYPES.map((v) => ({ value: v, label: L.type(v) })) },
      { key: "status", label: t("mantenimiento.fields.status"), type: "select", defaultValue: "pendiente", options: MAINTENANCE_STATUSES.map((v) => ({ value: v, label: L.status(v) })) },
      { key: "lastMaintenanceDate", label: t("mantenimiento.fields.date"), type: "date", required: true },
      { key: "scheduledDate", label: t("mantenimiento.fields.scheduledDate"), type: "date" },
      { key: "nextMaintenanceDate", label: t("mantenimiento.fields.nextMaintenanceDate"), type: "date" },
      { key: "nextMaintenanceOdometer", label: t("mantenimiento.fields.nextMaintenanceOdometer"), type: "number" },
      { key: "workshopId", label: t("mantenimiento.fields.workshop"), type: "select", options: workshops.map((w) => ({ value: w.id, label: `${w.name} · ${L.mode(w.mode)}` })) },
      { key: "workshopMode", label: t("mantenimiento.fields.workshopMode"), type: "select", defaultValue: "propio", options: WORKSHOP_MODES.map((v) => ({ value: v, label: L.mode(v) })) },
      { key: "workshopName", label: t("mantenimiento.fields.workshopName"), type: "text" },
      { key: "odometer", label: t("mantenimiento.fields.odometer"), type: "number" },
    ];
    if (isWorkOrder) {
      base.push(
        { key: "assignedMechanic", label: t("mantenimiento.fields.mechanic"), type: "text" },
        { key: "diagnosis", label: t("mantenimiento.fields.diagnosis"), type: "textarea" },
        {
          key: "serviceItems",
          label: t("mantenimiento.fields.services"),
          type: "repeat",
          addRowLabel: t("mantenimiento.addService"),
          repeatColumns: [
            { key: "name", label: t("mantenimiento.fields.serviceName") },
            { key: "quantity", label: t("mantenimiento.fields.quantity"), type: "number" },
            { key: "price", label: t("mantenimiento.fields.unitPrice"), type: "number" },
          ],
        },
        {
          key: "parts",
          label: t("mantenimiento.fields.parts"),
          type: "repeat",
          addRowLabel: t("mantenimiento.addPart"),
          repeatColumns: [
            { key: "name", label: t("mantenimiento.fields.partName") },
            { key: "quantity", label: t("mantenimiento.fields.quantity"), type: "number" },
            { key: "unitCost", label: t("mantenimiento.fields.unitCost"), type: "number" },
          ],
        },
        { key: "laborCost", label: t("mantenimiento.fields.laborCost"), type: "number", defaultValue: 0 },
        {
          key: "attachments",
          label: t("mantenimiento.fields.attachments"),
          type: "repeat",
          addRowLabel: t("mantenimiento.addAttachment"),
          repeatColumns: [
            { key: "name", label: t("mantenimiento.fields.attachmentName") },
            { key: "url", label: "URL" },
          ],
        }
      );
    } else {
      base.push({ key: "cost", label: t("mantenimiento.fields.cost"), type: "number", defaultValue: 0 });
    }
    base.push(
      { key: "photos", label: t("mantenimiento.fieldsExtra.photos"), type: "textarea", placeholder: "https://…" },
      { key: "maintenanceDescription", label: t("mantenimiento.fields.description"), type: "textarea", required: true }
    );
    return base;
  }, [t, L, vehicles, workshops, defaultType, isWorkOrder]);

  const toPayload = (values: Record<string, string>): Partial<MaintenanceRecord> => {
    // A linked workshop's configured mode wins over the manual select; the
    // backend enforces the same rule (own mechanic → free).
    const linked = workshops.find((w) => w.id === values.workshopId);
    const effectiveMode = linked?.mode || (values.workshopMode as MaintenanceRecord["workshopMode"]) || "propio";
    const payload: Partial<MaintenanceRecord> = {
      vehicleId: values.vehicleId,
      lastMaintenanceDate: values.lastMaintenanceDate,
      maintenanceDescription: values.maintenanceDescription,
      type: (values.type as MaintenanceType) || defaultType || "programado",
      status: (values.status as MaintenanceRecord["status"]) || "pendiente",
      workshopId: values.workshopId || null,
      workshopMode: effectiveMode,
      workshopName: linked?.name || values.workshopName || null,
      odometer: Number(values.odometer || 0),
      nextMaintenanceOdometer: Number(values.nextMaintenanceOdometer || 0),
      scheduledDate: values.scheduledDate || null,
      nextMaintenanceDate: values.nextMaintenanceDate || null,
      photos: (values.photos || "")
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean),
    };
    if (isWorkOrder) {
      payload.assignedMechanic = values.assignedMechanic || null;
      payload.diagnosis = values.diagnosis || null;
      payload.laborCost = Number(values.laborCost || 0);
      payload.serviceItems = parseRows<MaintenanceServiceItem>(values.serviceItems);
      payload.parts = parseRows<MaintenancePart>(values.parts);
      payload.attachments = parseRows<MaintenanceAttachment>(values.attachments);
    } else {
      payload.cost = effectiveMode === "externo" ? Number(values.cost || 0) : 0;
    }
    return payload;
  };

  return (
    <Modal
      title={record ? `${t("nuvexAccess.edit")} — ${record.maintenanceDescription ?? ""}` : t("mantenimiento.newRecord")}
      onClose={onClose}
      themeClasses={tc}
      content={
        <EntityForm
          fields={fields}
          initial={initialValues(record)}
          themeClasses={tc}
          submitLabel={record ? t("nuvexAccess.save") : t("nuvexAccess.create")}
          onCancel={onClose}
          onSubmit={async (values) => {
            await onSubmit(toPayload(values), record?.id);
            onClose();
          }}
        />
      }
    />
  );
}
